import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';

import type { FeatureContext } from '../../types';
import './api-tester.css';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'];

interface TestResponseDetails {
  url: string;
  method: string;
  status: number;
  statusText: string;
  ok: boolean;
  headers: Record<string, string>;
  contentType: string | null;
  rawBody: string;
  hasParsedBody: boolean;
  parsedBody: unknown;
  parseError?: string;
  bodyNotice?: string;
  durationMs: number | null;
}

export function ApiTesterFeature({
  apiBaseUrl,
  fallbackBaseUrl,
  shouldExplainFallback,
  acquireAccessToken
}: FeatureContext) {
  const [testPath, setTestPath] = useState('/health');
  const [testMethod, setTestMethod] = useState('GET');
  const [customMethod, setCustomMethod] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResponse, setTestResponse] = useState<TestResponseDetails | null>(null);

  function resolveTestUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const normalizedBase = apiBaseUrl.replace(/\/$/, '');
    const normalizedPath = path.replace(/^\//, '');
    return `${normalizedBase}/${normalizedPath}`;
  }

  function handleMethodChange(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setTestMethod(value);

    if (value !== 'CUSTOM') {
      setCustomMethod('');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setTestLoading(true);
    setTestError(null);
    setTestResponse(null);

    const trimmedPath = testPath.trim();
    const selectedMethod = testMethod === 'CUSTOM' ? customMethod : testMethod;
    const method = selectedMethod.trim().toUpperCase();

    if (!trimmedPath) {
      setTestError('Please provide an endpoint path or URL to test.');
      setTestLoading(false);
      return;
    }

    if (!method) {
      setTestError('Select or enter an HTTP method to use for the request.');
      setTestLoading(false);
      return;
    }

    if (!acquireAccessToken) {
      setTestError('Authentication is required before using the API tester.');
      setTestLoading(false);
      return;
    }

    let authorization: string | null = null;

    try {
      authorization = await acquireAccessToken();
    } catch (err) {
      setTestError(normalizeError(err));
      setTestLoading(false);
      return;
    }

    if (!authorization) {
      setTestError('Unable to acquire an access token for the request.');
      setTestLoading(false);
      return;
    }

    try {
      const url = resolveTestUrl(trimmedPath);
      const getNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const startTime = getNow();
      const requestHeaders: HeadersInit = authorization
        ? {
            Authorization: `Bearer ${authorization}`
          }
        : {};
      const response = await fetch(url, {
        method,
        headers: requestHeaders
      });
      const durationMs = Math.round(getNow() - startTime);
      const contentType = response.headers.get('content-type');
      const normalizedContentType = contentType?.toLowerCase() ?? null;
      const rawBody = await response.text();
      const trimmedBody = rawBody.trim();
      const responseHeaders = Object.fromEntries(response.headers.entries());

      let parsedBody: unknown = null;
      let hasParsedBody = false;
      let parseError: string | undefined;
      let bodyNotice: string | undefined;

      if (normalizedContentType && normalizedContentType.includes('application/json')) {
        if (trimmedBody === '') {
          bodyNotice = 'The server reported JSON but did not include a response body.';
        } else {
          try {
            parsedBody = JSON.parse(rawBody);
            hasParsedBody = true;
          } catch (err) {
            parseError = normalizeError(err);
          }
        }
      } else if (trimmedBody === '') {
        bodyNotice = 'The server response did not include a body.';
      }

      setTestResponse({
        url,
        method,
        status: response.status,
        statusText: response.statusText || (response.ok ? 'OK' : ''),
        ok: response.ok,
        headers: responseHeaders,
        contentType,
        rawBody,
        hasParsedBody,
        parsedBody,
        parseError,
        bodyNotice,
        durationMs: Number.isNaN(durationMs) ? null : durationMs
      });

      if (!response.ok) {
        setTestError(`Request completed with status ${response.status}.`);
      }
    } catch (err) {
      setTestError(normalizeError(err));
    } finally {
      setTestLoading(false);
    }
  }

  function normalizeError(reason: unknown): string {
    if (reason instanceof Error) {
      return reason.message;
    }

    try {
      return JSON.stringify(reason);
    } catch {
      return String(reason);
    }
  }

  const trimmedTestPath = testPath.trim();
  const selectedMethod = testMethod === 'CUSTOM' ? customMethod : testMethod;
  const normalizedMethod = selectedMethod.trim();
  const resolvedMethod = normalizedMethod.length > 0 ? normalizedMethod.toUpperCase() : null;
  const resolvedPreviewUrl = trimmedTestPath ? resolveTestUrl(trimmedTestPath) : null;

  return (
    <section className="page-section request-tester" aria-labelledby="request-tester-heading">
      <div className="request-card">
        <div className="request-card__header">
          <h2 id="request-tester-heading">Backend Request Tester</h2>
          <p>
            Enter an endpoint path (relative to the API base URL) or a full URL, choose the HTTP method, and Project Hermes will
            fire the request directly from the browser. Use this to quickly verify which endpoints are responding while
            debugging backend connectivity.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="request-form">
          <div className="field-group">
            <label>
              <span>Endpoint</span>
              <input
                type="text"
                value={testPath}
                onChange={(event) => setTestPath(event.target.value)}
                placeholder="/health"
                autoComplete="off"
              />
            </label>

            <label>
              <span>Method</span>
              <select value={testMethod} onChange={handleMethodChange}>
                {HTTP_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
                <option value="CUSTOM">Custom…</option>
              </select>
            </label>

            {testMethod === 'CUSTOM' && (
              <label>
                <span>Custom method</span>
                <input
                  type="text"
                  value={customMethod}
                  onChange={(event) => setCustomMethod(event.target.value)}
                  placeholder="e.g. UPDATE"
                  autoComplete="off"
                  className="uppercase-input"
                />
              </label>
            )}
          </div>

          <div className="request-preview" role="status" aria-live="polite">
            <div className="request-preview__label">Resolved request</div>
            <code>
              {resolvedMethod ? `${resolvedMethod} ` : ''}
              {resolvedPreviewUrl ?? 'Enter an endpoint to preview the full request URL.'}
            </code>
            <small>
              Requests default to <code>{apiBaseUrl}</code> unless a full URL is provided.
              {shouldExplainFallback && (
                <>
                  {' '}If the primary call is blocked by the browser, the app retries through <code>{fallbackBaseUrl}</code>.
                </>
              )}
            </small>
          </div>

          <button type="submit" disabled={testLoading}>
            {testLoading ? 'Sending…' : 'Send request'}
          </button>
        </form>

        {testError && (
          <p role="alert" className="request-error">
            {testError}
          </p>
        )}

        {testResponse && (
          <div className="response-output">
            <h3>Response details</h3>
            <div className="response-summary">
              <div className="response-summary__item">
                <div className="response-label">Status</div>
                <div className={`response-status ${testResponse.ok ? 'response-status--ok' : 'response-status--error'}`}>
                  {testResponse.status} {testResponse.statusText}
                </div>
              </div>
              <div className="response-summary__item">
                <div className="response-label">Method</div>
                <div className="response-value">{testResponse.method}</div>
              </div>
              <div className="response-summary__item">
                <div className="response-label">Duration</div>
                <div className="response-value">
                  {typeof testResponse.durationMs === 'number' ? `${testResponse.durationMs} ms` : '—'}
                </div>
              </div>
              <div className="response-summary__item">
                <div className="response-label">Content Type</div>
                <div className="response-value">{testResponse.contentType ?? 'Not provided'}</div>
              </div>
            </div>

            <details className="response-headers">
              <summary>Response headers</summary>
              <pre>{JSON.stringify(testResponse.headers, null, 2)}</pre>
            </details>

            {testResponse.parseError && (
              <p role="status" className="response-parse-error">
                Could not parse JSON body: {testResponse.parseError}. Showing raw response instead.
              </p>
            )}

            {testResponse.bodyNotice && (
              <p role="status" className="response-note">
                {testResponse.bodyNotice}
              </p>
            )}

            {testResponse.hasParsedBody && (
              <div className="response-body">
                <div className="response-label">Parsed body</div>
                <pre>{JSON.stringify(testResponse.parsedBody, null, 2)}</pre>
              </div>
            )}

            <div className="response-body">
              <div className="response-label">
                Raw body ({testResponse.rawBody.length} {testResponse.rawBody.length === 1 ? 'character' : 'characters'})
              </div>
              <pre>{testResponse.rawBody || '(empty body)'}</pre>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
